<template>
  <div class="flex items-center gap-2">
    <UmisisDropdownMenu
      split
      text="Launch"
      variant="outline"
      size="sm"
      class="max-w-56"
      :disabled="isLaunching || !canLaunch"
      @click="handlePrimaryAction"
    >
      <template #button-content>
        <span class="truncate">Launch: {{ defaultAssigneeLabel }}</span>
      </template>

      <DropdownMenuLabel>Launch task for:</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem v-if="hasDefaultAssignee" :disabled="isLaunching" @select="launchForDefaultAssignee">
        Default Assignee: {{ defaultAssigneeLabel }}
      </DropdownMenuItem>
      <DropdownMenuItem v-if="showMyselfOption" :disabled="isLaunching" @select="launchForMyself">
        Myself
      </DropdownMenuItem>
      <DropdownMenuItem :disabled="isLaunching" @select="openOtherUserModal">
        Other user
      </DropdownMenuItem>
    </UmisisDropdownMenu>

    <LaunchTaskTemplateModal
      ref="launchTaskTemplateModal"
      :task-template="taskTemplate"
      :current-user="currentUser"
      :initial-user-id="preferredLaunchUserId"
      :is-launching="isLaunching"
      :submit-launch-handler="launchWithOverrides"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import router from '../../../../../../../../resources/js/router/index.js'
import { useAuthStore } from '../../../../../../../../resources/js/stores/auth.js'
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import UmisisDropdownMenu from '../../../../../../../../resources/js/components/umisis/generic/UmisisDropdownMenu.vue'
import { useTaskTemplateStore } from '../../../stores/taskTemplateStore.js'
import {
  buildLaunchSuccessMessage,
  buildLaunchSuccessToastOptions,
  buildLaunchTargetOptions,
  buildTaskShowRoute,
  normalizeUserId,
  resolvePrimaryLaunchMode,
  resolveTaskTemplateLaunchAssignee,
} from '../../../utils/launchTaskTemplate.js'
import LaunchTaskTemplateModal from './LaunchTaskTemplateModal.vue'

const emit = defineEmits(['launched'])

const props = defineProps({
  taskTemplate: {
    type: Object,
    required: true,
  },
  context: {
    type: String,
    default: 'task-templates-list',
  },
})

const authStore = useAuthStore()
const launchTaskTemplateModal = ref(null)
const preferredLaunchUserId = ref(null)

const taskTemplateStore = useTaskTemplateStore(props.context)
const taskTemplateId = computed(() => Number(props.taskTemplate?.id ?? 0))
const currentUser = computed(() => authStore.currentUser)
const currentUserId = computed(() => normalizeUserId(currentUser.value?.id))
const defaultAssignee = computed(() => {
  return resolveTaskTemplateLaunchAssignee(props.taskTemplate, currentUser.value)
})
const launchTargetOptions = computed(() => {
  return buildLaunchTargetOptions({
    taskTemplate: props.taskTemplate,
    currentUser: currentUser.value,
  })
})
const defaultAssigneeLabel = computed(() => defaultAssignee.value.label)
const hasDefaultAssignee = computed(() => defaultAssignee.value.id !== null)
const showMyselfOption = computed(() => {
  return launchTargetOptions.value.some(({ key }) => key === 'myself')
})
const canLaunch = computed(() => Number.isInteger(taskTemplateId.value) && taskTemplateId.value > 0)
const isLaunching = computed(() => taskTemplateStore.isLaunching(taskTemplateId.value))
const hasVariables = computed(() => Array.isArray(props.taskTemplate?.variables_schema) && props.taskTemplate.variables_schema.length > 0)

const openLaunchModal = (userId = null) => {
  preferredLaunchUserId.value = userId
  launchTaskTemplateModal.value?.openModal?.()
}

const openOtherUserModal = () => {
  openLaunchModal(null)
}

const handlePrimaryAction = async () => {
  if (hasVariables.value) {
    if (resolvePrimaryLaunchMode(defaultAssignee.value.id) === 'default') {
      openLaunchModal(defaultAssignee.value.id)
      return
    }

    openLaunchModal()
    return
  }

  if (resolvePrimaryLaunchMode(defaultAssignee.value.id) === 'default') {
    await launchForDefaultAssignee()
    return
  }

  openOtherUserModal()
}

const showLaunchSuccess = (task, targetLabel) => {
  const taskRoute = buildTaskShowRoute(task?.id)

  toast.success(
    buildLaunchSuccessMessage(task, targetLabel),
    buildLaunchSuccessToastOptions({
      taskRoute,
      navigate: (route) => router.push(route),
    })
  )
}

const launchTemplate = async (payload = {}, targetLabel = defaultAssigneeLabel.value, options = {}) => {
  if (!canLaunch.value) {
    const invalidTaskTemplateError = new Error('Invalid task template id.')

    if (options.suppressErrors === true) {
      throw invalidTaskTemplateError
    }

    toast.error(invalidTaskTemplateError.message)

    return null
  }

  try {
    const task = await taskTemplateStore.launchTemplate(taskTemplateId.value, payload)
    showLaunchSuccess(task, targetLabel)
    emit('launched', task)

    return task
  } catch (error) {
    if (options.suppressErrors === true) {
      throw error
    }

    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.assigned_to_user_id?.[0]
      || error?.response?.data?.errors?.priority?.[0]
      || error?.response?.data?.errors?.deadline_at?.[0]
      || 'Unable to launch task template.'

    toast.error(errorMessage)

    return null
  }
}

const launchForDefaultAssignee = async () => {
  if (hasVariables.value) {
    openLaunchModal(defaultAssignee.value.id)
    return
  }

  await launchTemplate({}, defaultAssigneeLabel.value)
}

const launchForMyself = async () => {
  if (currentUserId.value === null) {
    toast.error('Unable to determine the current user.')
    return
  }

  if (hasVariables.value) {
    openLaunchModal(currentUserId.value)
    return
  }

  await launchTemplate({
    assigned_to_user_id: currentUserId.value,
  }, 'you')
}

const launchWithOverrides = async ({ payload, targetLabel }) => {
  return launchTemplate(payload, targetLabel, {
    suppressErrors: true,
  })
}
</script>
